/**
 * Snapshot-driven stream + forms-list hooks (spec §3 step 2/3, §8.2).
 *
 * Reads /api/streams (header-card data) and /api/streams/:stream/forms (form
 * rows) which are joins over the local sn_* tables and form_reviews. Both
 * refresh on filter changes; neither hits ServiceNow at request time.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  listStreamForms,
  listStreams,
  type StreamFormsFilters,
} from '../api';
import type { StreamFormRow, StreamSummary } from '../types';

type StreamsState = {
  streams: StreamSummary[];
  loading: boolean;
  error: string | null;
};

export function useStreams(
  includeOutOfScope: boolean = false,
): StreamsState & { refresh: () => void } {
  const [state, setState] = useState<StreamsState>({
    streams: [],
    loading: true,
    error: null,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    listStreams(includeOutOfScope)
      .then((rows) => {
        if (!cancelled) setState({ streams: rows, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : (e as Error)?.message ?? String(e);
        setState({ streams: [], loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [tick, includeOutOfScope]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, refresh };
}

type StreamFormsState = {
  forms: StreamFormRow[];
  loading: boolean;
  error: string | null;
};

export function useStreamForms(
  stream: string | undefined,
  filters: StreamFormsFilters,
): StreamFormsState & { refresh: () => void } {
  const [state, setState] = useState<StreamFormsState>({
    forms: [],
    loading: true,
    error: null,
  });
  const [tick, setTick] = useState(0);

  // Stable JSON key so a re-rendered filters object doesn't refetch endlessly.
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!stream) {
      setState({ forms: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    listStreamForms(stream, filters)
      .then((rows) => {
        if (!cancelled) setState({ forms: rows, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : (e as Error)?.message ?? String(e);
        setState({ forms: [], loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
    // filters intentionally excluded — filtersKey is the canonical signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, filtersKey, tick]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, refresh };
}
